import {featureSupport} from '../../../shared/feature-support.js';
import type { FeatureDefinition, TabKey } from './types';

const definitions: ReadonlyArray<Omit<FeatureDefinition,'support'>> = [
  {id:'care-message',tab:'care',title:'Besked til uret',icon:'sms',summary:'Send en kort tekst til urets skærm.'},
  {
    id: 'overview-customize', tab: 'overview', title: 'Tilpas Overblik', icon: 'customize',
    summary: 'Vælg kort, skjul dem og ændr rækkefølgen. Valget bevares ved designskift.',
    help: 'Gendan standard ændrer kun visningen — ikke trackerdata.',
  },
  {
    id: 'position-live', tab: 'position', title: 'Aktuel placering', icon: 'map',
    summary: 'Seneste gyldige trackerposition, kortnavigation, genindlæsning og ekstern Google Maps.',
  },
  {
    id: 'position-route', tab: 'position', title: 'Positionshistorik', icon: 'route',
    summary: 'Se gemte GPS-positioner på kortet. Vælg en dag, og afspil ruten i tidsrækkefølge.',
    help: 'Lyse strækninger er beregnede forbindelser mellem GPS-punkter. Lange pauser og usikre positionsspring forbindes ikke.',
  },
  {
    id: 'position-frequency', tab: 'position', title: 'Positionsinterval', icon: 'clock',
    summary: "Vælg, hvor ofte enheden skal opdatere sin position.",
  },
  {
    id: 'position-quality', tab: 'position', title: 'Forbindelse og kvalitet', icon: 'connection',
    summary: 'Undersøg GPS-dækning, regelmæssighed, pauser, seneste position og batteriforløb.',
  },
  {
    id: 'position-sources', tab: 'position', title: 'Positionskilder', icon: 'filter',
    summary: 'Se de positionskilder, som understøttes. Aktuelt vises gyldige GPS-positioner.',
    help: 'Mobilmaster og Wi-Fi kan give en omtrentlig placering, når GPS ikke er tilgængelig.',
  },
  {
    id: 'position-gps-zone', tab: 'position', title: 'GPS-sikkerhedszoner', icon: 'safe-zone',
    summary: 'Gem navngivne områder og vælg besked ved ankomst eller afgang.',
  },
  {
    id: 'position-wifi-zone', tab: 'position', title: 'Wi-Fi-sikkerhedszone', icon: 'wifi',
    summary: 'Knyt et trygt område til tre 2,4 GHz-netværksadresser.',
    help: "Vælg en indstilling, og tryk Gem.",
  },
  {
    id: 'care-measure', tab: 'care', title: 'Ny måling', icon: 'measure',
    summary: 'Start fælles måleforløb for puls, blodtryk og iltmætning eller mål temperatur.',
    help: 'Resultaterne er rapporterede målinger og er ikke en medicinsk diagnose.',
  },
  {
    id: 'care-history', tab: 'care', title: 'Målehistorik', icon: 'history',
    summary: 'Se puls, blodtryk, temperatur og iltmætning i grafer, og sammenlign perioder.',
  },
  {
    id: 'care-schedules', tab: 'care', title: 'Automatiske målinger', icon: 'schedule',
    summary: 'Indstil gentagelse og op til tre tidspunkter for temperatur på den aktuelle model.',
  },
  {
    id: 'care-thresholds', tab: 'care', title: 'Grænseværdier og advarsler', icon: 'warning',
    summary: "Vælg grænser og beskeder for målinger.",
    help: 'Appen foreslår ingen medicinske grænser.',
  },
  {
    id: 'care-medicine', tab: 'care', title: 'Medicinpåmindelser', icon: 'medicine',
    summary: 'Tre pladser med tekst, klokkeslæt, gentagelse, ugedage og til/fra.',
  },
  {
    id: 'care-call', tab: 'care', title: 'Ring til armbåndet', icon: 'call',
    summary: 'Start et almindeligt telefonopkald til armbåndets SIM-nummer.',
  },
  {
    id: 'care-voice', tab: 'care', title: 'Talebeskeder', icon: 'voice',
    summary: 'Optag, gennemlyt, annullér, send, modtag og afspil talebeskeder.',
    help: 'Talebeskeder er endnu ikke klar til brug.',
  },
  {
    id: 'care-callback', tab: 'care', title: 'Bed om tilbagekald', icon: 'callback',
    summary: 'Bed tydeligt armbåndet om at ringe til et bekræftet telefonnummer.',
    help: "Aftal opkaldet med personen, som bærer enheden.",
  },
  {
    id: 'events-current', tab: 'events', title: 'Aktuelle hændelser', icon: 'event',
    summary: 'SOS, fald, aftagning, lavt batteri, zoner, sundhed og ukendte hændelser.',
    help: 'At markere en hændelse som håndteret betyder kun, at en bruger har reageret.',
  },
  {
    id: 'events-archive', tab: 'events', title: 'Hændelseshistorik', icon: 'history',
    summary: 'Vælg en periode og filtrér historikken efter hændelsestype.',
  },
  {
    id: 'events-notifications', tab: 'device', title: 'Telefonnotifikationer', icon: 'notification',
    summary: 'Vælg beskeder til telefonen, lyd og vibration.',
    help: "Vælg beskeder til telefonen, lyd og vibration.",
  },
  {
    id: 'activity-analysis', tab: 'activity', title: 'Udvikling og analyse', icon: 'chart',
    summary: 'Skift mellem dag, uge, måned og år, og vælg nøgletal til grafen.',
  },
  {
    id: 'activity-records', tab: 'activity', title: 'Personlige rekorder', icon: 'trophy',
    summary: 'Se den valgte periodes bedste skridt, distance, aktive tid og GPS-segment.',
    help: 'Høj GPS-hastighed kan skyldes transport og kaldes ikke løbehastighed.',
  },
  {
    id: 'activity-profile', tab: 'activity', title: 'Aktivitetsprofil', icon: 'profile',
    summary: 'Redigér skridtlængde og vægt til beregning af estimater.',
    help: 'Ændringer kan genberegne historiske estimater, men ændrer ikke oprindelige GPS-registreringer.',
  },
  {
    id: 'activity-device-steps', tab: 'activity', title: 'Skridttæller', icon: 'steps',
    summary: 'Se armbåndets egne tal, slå registrering til eller fra og vælg tidsrum.',
  },
  {
    id: 'device-info', tab: 'device', title: 'Enhed og SIM', icon: 'sim',
    summary: 'Navn, IMEI / producent-ID, model, telefonnummer, SIM-ICCID, appversion og firmwareversion.',
  },
  {
    id: 'device-find', tab: 'device', title: 'Find armbåndet', icon: 'find-device',
    summary: 'Bed armbåndet ringe i cirka ét minut, og markér det fundet i appen.',
    help: '“Fundet” stopper ikke lyden på afstand. Lyden stopper på armbåndet eller ved timeout.',
  },
  {
    id: 'device-sos', tab: 'device', title: 'SOS-kontakter', icon: 'sos',
    summary: 'Tre prioriterede SOS-numre. Armbåndet har fast ringetid og understøtter ikke flere pladser.',
    help: 'Telefonsvarerhåndtering og SMS-levering til alle kontakter er ikke garanteret.',
  },
  {
    id: 'device-phonebook', tab: 'device', title: 'Telefonbog', icon: 'phonebook',
    summary: 'Gem op til 15 almindelige kontakter én plads ad gangen.',
    help: 'Telefonbogen er adskilt fra SOS-listen. Plads 1 og 2 bruges af V48-knapperne. Kontakter kan overskrives; sletning og billeder understøttes ikke.',
  },
  {
    id: 'device-sms', tab: 'device', title: 'SMS-alarmer', icon: 'sms',
    summary: "Vælg, hvilke SMS-beskeder enheden skal sende.",
  },
  {
    id: 'device-wrist', tab: 'device', title: 'Aftagningsalarm', icon: 'wrist',
    summary: "Vælg besked, når enheden tages af.",
  },
  {
    id: 'device-fall', tab: 'care', title: 'Faldalarm', icon: 'fall',
    summary: 'Slå faldregistrering og opkald til/fra og vælg ét af seks følsomhedsniveauer.',
    help: "Ingen indstilling kan sikre, at alle fald registreres.",
  },
  {
    id: 'device-sound', tab: 'device', title: 'Lyd og svar på opkald', icon: 'sound',
    summary: 'Vælg lyd/vibration og separat svarmåde for indgående opkald.',
  },
  {
    id: 'device-clock', tab: 'device', title: 'Vækkeur og tale', icon: 'speaking-clock',
    summary: 'Tre vækkeure samt til/fra for engelsk taleur og oplæsning af indgående telefonnummer.',
  },
  {
    id: 'device-safe-mode', tab: 'device', title: 'Sikker tilstand', icon: 'safe-zone',
    summary: 'Blokér ukendte opkald. Telefonbogens numre og SOS-numre må fortsat ringe ind.',
  },
  {
    id: 'device-language-time', tab: 'device', title: 'Sprog, ur og tidszone', icon: 'language',
    summary: 'Engelsk armbåndstale og manuelt valg af sommer-/vintertid. Appens sprog vælges separat.',
  },
  {
    id: 'device-power', tab: 'device', title: 'Tidsplaner og strøm', icon: 'power',
    summary: 'Separate tænd/sluk-planer, genstart, slukning og fabriksnulstilling med bekræftelse.',
    help: "Din historik i Qtracker slettes ikke ved nulstilling af enheden.",
  },
  {
    id: 'device-account', tab: 'device', title: 'Kontoadgang', icon: 'account',
    summary: 'Login, session, logud, kontoadgangskode og separat sekscifret SMS-kontrolkode.',
    help: 'En ændret kontoadgangskode logger eksisterende kontosessioner ud. SMS-koden kan ikke hentes bagefter.',
  },
  {
    id: 'device-link', tab: 'device', title: 'Tilknyt armbånd', icon: 'link-device',
    summary: "Tilføj en enhed med dens ID eller QR-kode.",
  },
  {
    id: 'device-app-settings', tab: 'device', title: 'App-indstillinger', icon: 'appearance',
    summary: 'Appens sprog og udseende. Administrér enheder, konto og delt adgang fra Min profil.',
  },
  {
    id: 'device-help', tab: 'device', title: 'Hjælp', icon: 'help',
    summary: 'Få hjælp til at bruge din enhed og løse almindelige problemer.',
  },
] as const;

export const features: ReadonlyArray<FeatureDefinition> = definitions.map(feature=>({...feature,support:featureSupport(feature.id).status as FeatureDefinition['support']}));

const devicePriority=[
  'device-info','device-account','device-app-settings','device-help','device-language-time',
  'device-sound','device-sms','events-notifications','device-sos','device-phonebook',
  'device-find','device-wrist','device-clock','device-safe-mode','device-power','device-link',
] as const;
export const featuresForTab = (tab: TabKey) => features.filter((feature) => feature.tab === tab).sort((left,right)=>{
  if(tab!=="device")return 0;
  const a=devicePriority.indexOf(left.id as typeof devicePriority[number]),b=devicePriority.indexOf(right.id as typeof devicePriority[number]);
  return (a<0?devicePriority.length:a)-(b<0?devicePriority.length:b);
});
export const getFeature = (id: string) => features.find((feature) => feature.id === id);
